import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";

const LABELS = { x: "X", tiktok: "TikTok" };

export default function AdditionalSocialAccounts({ workspaceKey }) {
  const [platforms, setPlatforms] = useState({});
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const generation = useRef(0);
  const reload = async () => {
    const current = ++generation.current;
    const results = await Promise.allSettled(Object.keys(LABELS).map(async (provider) => {
      const { data } = await axios.get(`${API}/social/accounts/${provider}/status`);
      return [provider, data];
    }));
    if (current !== generation.current) return;
    const next = {};
    results.forEach((result, index) => {
      const provider = Object.keys(LABELS)[index];
      next[provider] = result.status === "fulfilled" ? result.value[1] : { error: true };
    });
    setPlatforms(next);
  };
  useEffect(() => {
    setPlatforms({});
    reload();
    return () => { generation.current += 1; };
    // The parent remounts this panel when the workspace changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceKey]);

  const run = async (provider, action, accountId) => {
    if (pending.current) return;
    if (action === "disconnect" && !window.confirm("Disconnect this account and revoke Zynthoro access?")) return;
    pending.current = true;
    setBusy(true);
    try {
      const suffix = action === "start" ? "start" : `${accountId}/${action}`;
      const { data } = await axios.post(`${API}/social/accounts/${provider}/${suffix}`);
      if (action === "start") {
        const url = new URL(data.authorize_url);
        const expected = provider === "x" ? "x.com" : "www.tiktok.com";
        if (url.protocol !== "https:" || url.hostname !== expected) throw new Error();
        window.location.assign(url.href);
      } else {
        toast.success(action === "refresh" ? "Authorisation renewed." : "Account disconnected.");
        await reload();
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The account operation could not be completed. Check the account status before retrying.");
      await reload();
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {Object.entries(LABELS).map(([provider, label]) => {
        const state = platforms[provider];
        return (
          <section key={provider} className="border border-[#eee] rounded-xl p-4" data-testid={`mc-account-${provider}`}>
            <h3 className="font-semibold text-sm">{label} accounts</h3>
            <p className="text-xs text-[#666] mt-1">
              {!state ? "Loading…" : state.error ? "Account status could not be loaded."
                : state.configured ? "Connect your own account. Publishing is not enabled."
                : "Connection prepared; activation follows after deployment."}
            </p>
            <button className="zy-btn-outline mt-3" disabled={busy || !state?.configured}
              onClick={() => run(provider, "start")}>
              Add {label} account
            </button>
            {(state?.accounts || []).map((account) => (
              <div key={account.id} className="mt-3 text-sm border-t pt-3">
                <p className="font-medium">{account.account_name}</p>
                <p className="text-xs text-[#666]">
                  {account.state === "connected" ? "Connected" :
                    account.state === "reauthorisation_required" ? "Reconnect required" :
                    "Account operation requires review"}
                </p>
                {state.configured && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {account.state === "connected" && (
                      <button className="zy-btn-outline" disabled={busy} onClick={() => run(provider, "refresh", account.id)}>
                        Renew authorisation
                      </button>
                    )}
                    {["connected", "reauthorisation_required"].includes(account.state) && (
                      <button className="zy-btn-outline" disabled={busy} onClick={() => run(provider, "disconnect", account.id)}>
                        Disconnect
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
